// The conditions of one piece as rows for the inspector. A row is the badge
// as the board draws it, then the words, so the player learns the badge from
// the row. Until 2026-10-11 a row was a paper pill with an ink mark. Garrett:
// "these big pills in the tooltip aren't great".

import { casterById } from '../content/casters';
import { ABILITY_NAMES, KIND_NAMES } from '../engine/abilities';
import { activeTraits, HEART_PATH } from '../engine/traits';
import type { LearnSource, Piece } from '../engine/types';
import { el } from '../lib/dom';
import { svg } from '../lib/svg';
import { spellById } from '../spells/library';
import {
  abilityTone,
  badgeClass,
  CROWN_PATH,
  createAbilitySymbol,
  createBadgeDisc,
  createCasterBadge,
  createEnergySymbol,
  createMarkSymbol,
  createPitchSymbol,
  createTraitSymbol,
  DISC_BOX_RADIUS,
} from './Badges';

/** "2 lives" becomes "2 lives." and "stopped for 2 more turns" becomes "Stopped for 2 more turns." */
const asSentence = (words: string): string => `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;

const tagOfFace = (tone: string, face: SVGGElement, words: string): HTMLLIElement =>
  el('li', { className: 'tag' }, [
    svg(
      'svg',
      {
        class: 'tag__icon',
        viewBox: `${-DISC_BOX_RADIUS} ${-DISC_BOX_RADIUS} ${DISC_BOX_RADIUS * 2} ${DISC_BOX_RADIUS * 2}`,
        'aria-hidden': 'true',
      },
      [svg('g', { class: badgeClass(tone) }, [face])],
    ),
    el('span', { className: 'tag__words' }, [asSentence(words)]),
  ]);

const tag = (tone: string, symbol: SVGElement, words: string, fuse = false): HTMLLIElement =>
  tagOfFace(tone, createBadgeDisc(symbol, fuse), words);

/**
 * How the piece learned an ability, after "learned diagonals". Garrett,
 * 2026-10-15: "Learned straight lines by defeating Queen etc so its clear how
 * it happened". Plain words, not final copy. No kind name starts with a
 * vowel, so "a" fits each.
 */
const sourceWords = (source: LearnSource | undefined): string => {
  if (!source) return '';
  if (source.how === 'spell') return ` from ${spellById(source.spellId).name}`;
  const kind = KIND_NAMES[source.kind];
  if (source.how === 'capture') return ` by defeating a ${kind}`;
  if (source.how === 'inherit') return ` from a fallen ${kind} beside it`;
  return ` from a ${kind} beside it`;
};

/** The rows for the roles of a piece in Godsmode. Plain words, not final copy. */
const roleTags = (piece: Piece): HTMLLIElement[] => [
  ...(piece.casterId !== null
    ? [
        tagOfFace(
          'caster',
          createCasterBadge(piece.casterId),
          `carries the caster ${casterById(piece.casterId).name}`,
        ),
      ]
    : []),
  ...(piece.energyCap > 0
    ? [
        tag(
          piece.energy > 0 ? 'energy' : 'energy-empty',
          createEnergySymbol(),
          `holds ${piece.energy} of ${piece.energyCap} energy`,
        ),
      ]
    : []),
  ...(piece.pitch !== null
    ? [
        tag(
          `pitch-${piece.pitch}`,
          createPitchSymbol(piece.pitch),
          `gives the ${piece.pitch} pitch`,
        ),
      ]
    : []),
];

export const createConditionTags = (piece: Piece): HTMLLIElement[] => [
  ...roleTags(piece),
  ...(piece.lives > 1 ? [tag('life', createMarkSymbol(HEART_PATH), `${piece.lives} lives`)] : []),
  ...(piece.royal && piece.kind !== 'king'
    ? [tag('royal', createMarkSymbol(CROWN_PATH), 'royal by adoption')]
    : []),
  ...piece.gained.map((ability) =>
    tag(
      abilityTone(ability),
      createAbilitySymbol(ability, piece.owner),
      `learned ${ABILITY_NAMES[ability]}${sourceWords(piece.learnedFrom[ability])}`,
    ),
  ),
  ...activeTraits(piece).map((trait) =>
    tag(trait.tone, createTraitSymbol(piece, trait), trait.describe(piece), trait.fuse),
  ),
];
